/**
 * Agentic activity feed model (v2.1.0 P3-06, design §8.1 / plan P3-06).
 *
 * A pure reducer over the typed stream events that concern the activity feed:
 * `agent_started` opens a group, `handoff` renders a transition,
 * `tool_call`/`status` add rows to their agent's group, `done` marks the feed
 * collapsible. Shared by the web app and the embed surfaces so both render
 * the identical feed (plan P3-06 parity requirement) — keep all feed
 * semantics HERE, not in the consumers.
 *
 * The reducer is immutable: it returns a new feed object when the event
 * changed anything and the same reference when it did not, so React state
 * setters can be handed the result directly.
 */

export interface FeedAgentTag {
  agentId: string;
  name: string;
}

/**
 * The subset of stream events the feed consumes. Extra fields are tolerated
 * and ignored so callers can pass raw decoded events straight through.
 */
export interface FeedEvent {
  type: string;
  agentTag?: FeedAgentTag;
  /** agent_started */
  role?: string;
  depth?: number;
  /** handoff */
  from?: FeedAgentTag;
  to?: FeedAgentTag;
  /** status */
  text?: string;
  /** tool_call */
  tool?: string;
  summary?: string;
  [extra: string]: unknown;
}

export interface ActivityGroup {
  kind: 'group';
  agentId: string;
  name: string;
  role?: string;
  depth: number;
  rows: string[];
}

export interface ActivityHandoff {
  kind: 'handoff';
  fromName: string;
  toName: string;
}

export type ActivityFeedItem = ActivityGroup | ActivityHandoff;

export interface ActivityFeed {
  items: ActivityFeedItem[];
  /** Total row + handoff count — the "N steps" in the collapsed summary. */
  steps: number;
  /** Set by the done event; the renderer collapses the feed. */
  done: boolean;
}

export function emptyActivityFeed(): ActivityFeed {
  return { items: [], steps: 0, done: false };
}

function lastGroupFor(
  items: ActivityFeedItem[],
  agentId: string | undefined,
): ActivityGroup | null {
  for (let i = items.length - 1; i >= 0; i--) {
    const item = items[i];
    if (item.kind !== 'group') continue;
    // Untagged events (defensive) land in the most recent group.
    if (agentId === undefined || item.agentId === agentId) return item;
  }
  return null;
}

/**
 * Fold one stream event into the feed. Events the feed does not model
 * (token, citation, reset, error, ask_user, …) return the feed unchanged.
 */
export function applyActivityEvent(
  feed: ActivityFeed,
  event: FeedEvent | null | undefined,
): ActivityFeed {
  if (!event || typeof event.type !== 'string') return feed;

  switch (event.type) {
    case 'agent_started': {
      const tag = event.agentTag;
      if (!tag?.agentId) return feed;
      // The recursion loop never re-emits agent_started, but be idempotent
      // anyway: an immediately repeated start for the same agent is a no-op.
      const last = feed.items[feed.items.length - 1];
      if (last?.kind === 'group' && last.agentId === tag.agentId && last.rows.length === 0) {
        return feed;
      }
      const group: ActivityGroup = {
        kind: 'group',
        agentId: tag.agentId,
        name: tag.name || 'Assistant',
        role: typeof event.role === 'string' ? event.role : undefined,
        depth: typeof event.depth === 'number' ? event.depth : 0,
        rows: [],
      };
      return { ...feed, items: [...feed.items, group] };
    }

    case 'handoff': {
      const item: ActivityHandoff = {
        kind: 'handoff',
        fromName: event.from?.name || 'Assistant',
        toName: event.to?.name || 'a specialist',
      };
      return { ...feed, items: [...feed.items, item], steps: feed.steps + 1 };
    }

    case 'status':
    case 'tool_call': {
      const text =
        event.type === 'status'
          ? typeof event.text === 'string'
            ? event.text
            : ''
          : typeof event.summary === 'string'
            ? event.summary
            : '';
      if (!text) return feed;
      const items = feed.items.slice();
      let group = lastGroupFor(items, event.agentTag?.agentId);
      if (!group) {
        // Event arrived before any agent_started (legacy producer or replay):
        // open an implicit group so the row is never dropped.
        group = {
          kind: 'group',
          agentId: event.agentTag?.agentId || 'agent',
          name: event.agentTag?.name || 'Assistant',
          depth: 0,
          rows: [],
        };
        items.push(group);
      } else {
        const idx = items.indexOf(group);
        group = { ...group, rows: group.rows.slice() };
        items[idx] = group;
      }
      group.rows.push(text);
      return { ...feed, items, steps: feed.steps + 1 };
    }

    case 'done':
      return feed.done ? feed : { ...feed, done: true };

    default:
      return feed;
  }
}

/** Number of distinct agents that appear in the feed. */
export function feedAgentCount(feed: ActivityFeed): number {
  const ids = new Set<string>();
  for (const item of feed.items) {
    if (item.kind === 'group') ids.add(item.agentId);
  }
  return ids.size;
}
